import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const WellnessFilterPanel = ({ onFilterChange, centers, onClose }) => {
  const [filters, setFilters] = useState({
    priceRange: { min: 0, max: 1000 },
    types: [],
    categories: [],
    amenities: [],
    certifications: [],
    duration: [],
    rating: 0,
    availability: 'all'
  });

  const [isExpanded, setIsExpanded] = useState({
    price: true,
    type: true,
    categories: false,
    amenities: false,
    certifications: false,
    duration: false,
    rating: false,
    availability: false
  });

  // Extract unique values from centers data
  const extractUniqueValues = () => {
    const types = [...new Set(centers?.map(center => center.type) || [])];
    const categories = [...new Set(centers?.flatMap(center => center.categories || []) || [])];
    const amenities = [...new Set(centers?.flatMap(center => center.amenities || []) || [])];
    const certifications = [...new Set(centers?.flatMap(center => center.certifications || []) || [])];
    const durations = [...new Set(centers?.map(center => center.duration) || [])];

    return { types, categories, amenities, certifications, durations };
  };

  const { types, categories, amenities, certifications, durations } = extractUniqueValues();

  const toggleSection = (section) => {
    setIsExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleFilterChange = (filterType, value, isChecked = null) => {
    let newFilters = { ...filters };

    if (filterType === 'priceRange') {
      newFilters.priceRange = value;
    } else if (filterType === 'rating') {
      newFilters.rating = value;
    } else if (filterType === 'availability') {
      newFilters.availability = value;
    } else if (isChecked !== null) {
      // Handle array filters (checkboxes)
      if (isChecked) {
        newFilters[filterType] = [...filters[filterType], value];
      } else {
        newFilters[filterType] = filters[filterType].filter(item => item !== value);
      }
    }

    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      priceRange: { min: 0, max: 1000 },
      types: [],
      categories: [],
      amenities: [],
      certifications: [],
      duration: [],
      rating: 0,
      availability: 'all'
    };
    setFilters(clearedFilters);
    onFilterChange?.(clearedFilters);
  };

  const hasActiveFilters = () => {
    return (
      filters.types.length > 0 ||
      filters.categories.length > 0 ||
      filters.amenities.length > 0 ||
      filters.certifications.length > 0 ||
      filters.duration.length > 0 ||
      filters.rating > 0 ||
      filters.availability !== 'all' ||
      filters.priceRange.min > 0 ||
      filters.priceRange.max < 1000
    );
  };

  const FilterSection = ({ title, isExpanded, onToggle, children }) => (
    <div className="border-b border-border pb-6">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full mb-4"
      >
        <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
        <Icon
          name="ChevronDown"
          size={16}
          className={`text-text-secondary transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>
      )}
    </div>
  );

  const CheckboxGroup = ({ items, selectedItems, onItemChange, formatLabel = (item) => item }) => (
    <div className="space-y-3">
      {items.map((item) => (
        <label key={item} className="flex items-center space-x-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={selectedItems.includes(item)}
            onChange={(e) => onItemChange(item, e.target.checked)}
            className="w-4 h-4 text-primary border border-border rounded focus:ring-2 focus:ring-primary focus:border-primary" // Changed from blue-600 and blue-500
          />
          <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors duration-200">
            {formatLabel(item)}
          </span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-elevation-2 border border-border p-6 sticky top-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-text-primary font-heading">Filters</h2>
        <div className="flex items-center space-x-2">
          {hasActiveFilters() && (
            <button
              onClick={clearFilters}
              className="text-sm text-primary hover:text-secondary font-medium" // Changed from blue-600 and blue-700
            >
              Clear all
            </button>
          )}
          <button
            onClick={onClose}
            className="md:hidden p-1 hover:bg-background-light rounded-md" // Changed from secondary-100
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      </div>

      {/* Filter Sections */}
      <div className="space-y-6">
        {/* Price Range */}
        <FilterSection
          title="Price Range"
          isExpanded={isExpanded.price}
          onToggle={() => toggleSection('price')}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm text-text-secondary">
              <span>${filters.priceRange.min}</span>
              <span>${filters.priceRange.max}+</span>
            </div>
            <div className="relative">
              <input
                type="range"
                min="0"
                max="1000"
                value={filters.priceRange.min}
                onChange={(e) => handleFilterChange('priceRange', {
                  ...filters.priceRange,
                  min: parseInt(e.target.value)
                })}
                className="w-full h-2 bg-background-medium rounded-lg appearance-none cursor-pointer" // Changed from blue-100
              />
              <input
                type="range"
                min="0"
                max="1000"
                value={filters.priceRange.max}
                onChange={(e) => handleFilterChange('priceRange', {
                  ...filters.priceRange,
                  max: parseInt(e.target.value)
                })}
                className="w-full h-2 bg-background-medium rounded-lg appearance-none cursor-pointer absolute top-0" // Changed from blue-100
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Min Price</label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={filters.priceRange.min}
                  onChange={(e) => handleFilterChange('priceRange', {
                    ...filters.priceRange,
                    min: parseInt(e.target.value) || 0
                  })}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Max Price</label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={filters.priceRange.max}
                  onChange={(e) => handleFilterChange('priceRange', {
                    ...filters.priceRange,
                    max: parseInt(e.target.value) || 1000
                  })}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm"
                />
              </div>
            </div>
          </div>
        </FilterSection>

        {/* Center Types */}
        <FilterSection
          title="Center Types"
          isExpanded={isExpanded.type}
          onToggle={() => toggleSection('type')}
        >
          <CheckboxGroup
            items={types}
            selectedItems={filters.types}
            onItemChange={(item, checked) => handleFilterChange('types', item, checked)}
          />
        </FilterSection>

        {/* Wellness Categories */}
        <FilterSection
          title="Wellness Categories"
          isExpanded={isExpanded.categories}
          onToggle={() => toggleSection('categories')}
        >
          <CheckboxGroup
            items={categories}
            selectedItems={filters.categories}
            onItemChange={(item, checked) => handleFilterChange('categories', item, checked)}
            formatLabel={(item) => item.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          />
        </FilterSection>

        {/* Amenities */}
        <FilterSection
          title="Amenities & Features"
          isExpanded={isExpanded.amenities}
          onToggle={() => toggleSection('amenities')}
        >
          <CheckboxGroup
            items={amenities.slice(0, 8)} // Show top 8 amenities
            selectedItems={filters.amenities}
            onItemChange={(item, checked) => handleFilterChange('amenities', item, checked)}
          />
        </FilterSection>

        {/* Practitioner Certifications */}
        <FilterSection
          title="Practitioner Certifications"
          isExpanded={isExpanded.certifications}
          onToggle={() => toggleSection('certifications')}
        >
          <CheckboxGroup
            items={certifications}
            selectedItems={filters.certifications}
            onItemChange={(item, checked) => handleFilterChange('certifications', item, checked)}
          />
        </FilterSection>

        {/* Duration */}
        <FilterSection
          title="Program Duration"
          isExpanded={isExpanded.duration}
          onToggle={() => toggleSection('duration')}
        >
          <CheckboxGroup
            items={durations}
            selectedItems={filters.duration}
            onItemChange={(item, checked) => handleFilterChange('duration', item, checked)}
          />
        </FilterSection>

        {/* Rating */}
        <FilterSection
          title="Minimum Rating"
          isExpanded={isExpanded.rating}
          onToggle={() => toggleSection('rating')}
        >
          <div className="space-y-3">
            {[4.5, 4.0, 3.5, 3.0].map((rating) => (
              <label key={rating} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="radio"
                  name="rating"
                  value={rating}
                  checked={filters.rating === rating}
                  onChange={() => handleFilterChange('rating', rating)}
                  className="w-4 h-4 text-primary border border-border focus:ring-2 focus:ring-primary focus:border-primary" // Changed from blue-600 and blue-500
                />
                <div className="flex items-center space-x-1">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Icon
                        key={i}
                        name="Star"
                        size={14}
                        className={`${
                          i < Math.floor(rating)
                            ? 'text-yellow-400 fill-current' :'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-text-secondary">{rating}+</span>
                </div>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* Availability */}
        <FilterSection
          title="Availability"
          isExpanded={isExpanded.availability}
          onToggle={() => toggleSection('availability')}
        >
          <div className="space-y-3">
            {[
              { value: 'all', label: 'All Centers' },
              { value: 'available', label: 'Available Now' },
              { value: 'limited', label: 'Limited Availability' },
              { value: 'booking-required', label: 'Booking Required' }
            ].map((option) => (
              <label key={option.value} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="radio"
                  name="availability"
                  value={option.value}
                  checked={filters.availability === option.value}
                  onChange={() => handleFilterChange('availability', option.value)}
                  className="w-4 h-4 text-primary border border-border focus:ring-2 focus:ring-primary focus:border-primary" // Changed from blue-600 and blue-500
                />
                <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors duration-200">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </FilterSection>
      </div>
    </div>
  );
};

export default WellnessFilterPanel;
